import React, { useState, useEffect, forwardRef } from "react";
import { useHistory } from "react-router-dom";

import {
  Button,
  Dialog,
  IconButton,
  Typography,
  Slide,
  Grid,
  CircularProgress,
  Box,
  TextField,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  DialogActions,
  DialogTitle,
  DialogContent,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import WardAddEditRoomDialog from "./WardAddEditRoomDialog";
import { apiGet, API } from "@services/API";
import { useSnackbar } from "notistack";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function headers() {
  let headers = {};
  let token = localStorage.getItem("covid19_token");
  let mfa_token = localStorage.getItem("mfa_token");
  if (token && token.length > 0 && mfa_token && mfa_token.length > 0) {
    headers = { "x-access-token": token };
  }
  return headers;
}

export default function WardAddEditDialog({
  isVisible,
  onClose,
  onSave,
  hospitalID,
  wardID,
  ...props
}) {
  let history = useHistory();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);

  const [wardName, setWardName] = useState("");
  const [wardLocation, setWardLocation] = useState("");
  const [wardRooms, setWardRooms] = useState([]);

  const [roomID, setRoomID] = useState(null);
  const [roomName, setRoomName] = useState("");
  const [roomGender, setRoomGender] = useState("");
  const [beds, setBeds] = useState([]);

  const [editingBedIndex, setEditingBedIndex] = useState(-1);

  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);

  const [roomToDeleteIndex, setRoomToDeleteIndex] = useState(-1);
  const [editingRoomIndex, setEditingRoomIndex] = useState(-1);

  useEffect(() => {
    if (wardID !== null) {
      apiGet(`/ward/${wardID}`, { headers: headers() }, history)
        .then((response) => {
          const wardDetails = response.data.wards[0];

          setWardName(wardDetails.facility);
          setWardLocation(wardDetails.location);

          const roomsWithParsedBeds = response.data.rooms.map((room) => ({
            ...room,
            all_beds: JSON.parse(room.all_beds).map((bed) => ({
              ...bed,
              deleted: false,
            })),
          }));

          setWardRooms(roomsWithParsedBeds);

          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
        });
    } else {
      setTimeout(() => {
        setLoading(false);
      }, 200);
    }
  }, []);

  const showSnackBar = (type, title, subtitle) => {
    const options = { variant: type };

    enqueueSnackbar(
      <div style={{ display: "inline-grid" }}>
        <Typography data-test={"snack-title"} variant="h6">
          {title}
        </Typography>
        <Typography data-test={"snack-text"} variant="subtitle1">
          {subtitle}
        </Typography>
      </div>,
      options
    );
  };

  const isNullOrEmpty = (value) => {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === "string" && value.trim().toLowerCase() === "null") {
      return true;
    }

    if (Array.isArray(value) && value.length === 0) {
      return true;
    }

    if (typeof value === "string" && value.trim() === "") {
      return true;
    }

    if (typeof value === "number" && isNaN(value)) {
      return true;
    }

    return false;
  };

  const handleSave = () => {
    if (isNullOrEmpty(wardName)) {
      showSnackBar("error", "Failure", "Ward name can't be empty.");
    } else if (isNullOrEmpty(wardLocation)) {
      showSnackBar("error", "Failure", "Ward location can't be empty.");
    } else if (wardRooms.length === 0) {
      showSnackBar(
        "error",
        "Failure",
        "Need to add at least 1 room to a ward."
      );
    } else {
      const admin_user_id = localStorage.getItem("user_id");
      const admin_facility_id = localStorage.getItem("user_facility_id");

      const message = {
        admin_user_id: Number(admin_user_id),
        admin_facility_id: Number(admin_facility_id),
        facility_id: Number(wardID),
        hospital_id: Number(hospitalID),
        facility: wardName,
        location: wardLocation,
        rooms: wardRooms,
      };

      API.post("/ward", message, {
        headers: headers(),
      })
        .then((response) => {
          showSnackBar(
            "success",
            "Success",
            "Successfully saved ward details."
          );

          onSave();
        })
        .catch((error) => {
          showSnackBar("error", "Failure", "Failed to save ward details.");
        });
    }
  };

  const handleClose = () => {
    onClose();
  };

  const addRoom = () => {
    setRoomID(null);
    setRoomName("");
    setRoomGender("");
    setBeds([]);
    setEditingRoomIndex(-1);
    setRoomDialogOpen(true);
  };

  const handleEditRoom = (roomIndex) => {
    const roomToEdit = wardRooms[roomIndex];
    setRoomID(roomToEdit.id);
    setRoomName(roomToEdit.name);
    setRoomGender(roomToEdit.gender);
    setBeds(roomToEdit.all_beds);
    setEditingRoomIndex(roomIndex);
    setRoomDialogOpen(true);
  };

  const handleSaveRoom = () => {
    if (roomName.trim() === "") {
      // Handle validation for room name
      return;
    }

    const roomData = {
      id: roomID,
      name: roomName,
      gender: roomGender,
      all_beds: beds,
    };

    if (editingRoomIndex !== -1) {
      // Editing an existing room
      const updatedRooms = [...wardRooms];
      updatedRooms[editingRoomIndex] = roomData;
      setWardRooms(updatedRooms);
    } else {
      // Adding a new room
      setWardRooms([...wardRooms, roomData]);
    }

    setRoomName("");
    setRoomGender("");
    //setBeds([]);
    setEditingRoomIndex(-1);
    setRoomDialogOpen(false);
  };

  const handleDeleteRoom = (roomIndex) => {
    const roomToEdit = wardRooms[roomIndex];

    const hasNonNullPatient = roomToEdit.all_beds.some(
      (bed) => bed.patient_id !== null
    );

    if (hasNonNullPatient) {
      showSnackBar(
        "error",
        "Failure",
        "Room cannot be deleted as it has at least one patient assigned."
      );
    } else {
      setRoomToDeleteIndex(roomIndex);
      setConfirmDeleteDialogOpen(true);
    }
  };

  const confirmDeleteRoom = () => {
    if (roomToDeleteIndex !== -1) {
      const updatedRooms = [...wardRooms];
      const roomToDelete = updatedRooms[roomToDeleteIndex];

      if (roomToDelete.id === null) {
        updatedRooms.splice(roomToDeleteIndex, 1);
      } else {
        roomToDelete.all_beds.forEach((bed) => {
          bed.deleted = true;
        });
        roomToDelete.deleted = true;
      }

      setWardRooms(updatedRooms);
    }

    setConfirmDeleteDialogOpen(false);
    setRoomToDeleteIndex(-1);
  };

  const countNotDeletedRooms = (rooms) => {
    let result = rooms.filter((room) => !room.deleted).length;
    return result;
  };

  return (
    <Dialog
      open={isVisible}
      onClose={handleClose}
      TransitionComponent={Transition}
      PaperProps={{
        style: {
          backgroundColor: "#FFF",
          boxShadow: "none",
        },
      }}
    >
      <Box
        style={{
          margin: 15,
          //backgroundColor: "#FCFCFC",
        }}
      >
        {loading ? (
          <CircularProgress color="primary" />
        ) : (
          <>
            <Grid
              container
              spacing={2}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Grid
                item
                xs={12}
                md={12}
                style={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Typography data-test={"ward-add-edit-title"} variant="h6">
                  Ward Details
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                style={{
                  display: "flex",
                  //justifyContent: "center",
                }}
              >
                <TextField
                  data-test={"ward-add-edit-name"}
                  label="Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={wardName}
                  onChange={(e) => setWardName(e.target.value)}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                style={{
                  display: "flex",
                  //justifyContent: "center",
                }}
              >
                <TextField
                  data-test={"ward-add-edit-location"}
                  label="Location"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={wardLocation}
                  onChange={(e) => setWardLocation(e.target.value)}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={12}
                style={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Typography
                  data-test={"ward-add-edit-rooms-title"}
                  variant="subtitle1"
                >
                  Rooms
                </Typography>
              </Grid>

              {wardRooms !== undefined &&
              wardRooms !== null &&
              wardRooms.length > 0 ? (
                <Grid item xs={12} md={12}>
                  <List dense={true} data-test={`ward-add-edit-room-list`}>
                    {wardRooms.map((room, roomIndex) => (
                      <Paper
                        key={roomIndex}
                        elevation={2}
                        style={{
                          marginBottom: 8,
                          backgroundColor: room.deleted ? "#a5a5a5" : "#ffffff",
                        }}
                      >
                        <ListItem
                          data-test={`ward-add-edit-room-item-${roomIndex}`}
                        >
                          <ListItemText
                            data-test={"ward-add-edit-room-room-name"}
                            primary={
                              room.deleted ? (
                                <del>
                                  {!isNullOrEmpty(room.name)
                                    ? "Room: " + room.name
                                    : "Room: -"}
                                </del>
                              ) : !isNullOrEmpty(room.name) ? (
                                "Room: " + room.name
                              ) : (
                                "Room: -"
                              )
                            }
                            secondary={
                              !room.deleted && (
                                <>
                                  <Typography
                                    data-test={"ward-add-edit-room-gender-name"}
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    Gender:{" "}
                                    {room.gender === "male"
                                      ? "Male"
                                      : room.gender === "female"
                                      ? "Female"
                                      : room.gender === "either"
                                      ? "Either"
                                      : "-"}
                                  </Typography>

                                  <Typography
                                    data-test={"ward-add-edit-room-bed-names"}
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    Bed names:{" "}
                                    {room.all_beds !== undefined &&
                                    room.all_beds !== null &&
                                    room.all_beds.length > 0
                                      ? room.all_beds
                                          .filter((bed) => !bed.deleted)
                                          .map((bed, bedIndex) => bed.name)
                                          .join(", ")
                                      : "-"}
                                  </Typography>
                                </>
                              )
                            }
                          />
                          <ListItemSecondaryAction>
                            <>
                              {!room.deleted && (
                                <IconButton
                                  data-test={"ward-add-edit-room-edit-btn"}
                                  edge="end"
                                  aria-label="edit"
                                  style={{ marginRight: 8 }}
                                  onClick={() => handleEditRoom(roomIndex)}
                                >
                                  <EditIcon />
                                </IconButton>
                              )}

                              {!room.deleted &&
                                countNotDeletedRooms(wardRooms) > 1 && (
                                  <IconButton
                                    data-test={"ward-add-edit-room-delete-btn"}
                                    edge="end"
                                    aria-label="delete"
                                    onClick={() => handleDeleteRoom(roomIndex)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                            </>
                          </ListItemSecondaryAction>
                        </ListItem>
                      </Paper>
                    ))}
                  </List>
                </Grid>
              ) : (
                <Grid
                  item
                  xs={12}
                  md={12}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    data-test={"ward-rooms-empty-msg"}
                    variant="caption"
                  >
                    No records to display
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}></Grid>

              <Grid
                item
                xs={12}
                md={12}
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  flexDirection: "row",
                }}
              >
                <Button
                  data-test={"ward-add-edit-add-room-btn"}
                  variant="contained"
                  color="primary"
                  onClick={addRoom}
                >
                  Add Room
                </Button>
                <Button
                  data-test={"ward-add-edit-cancel-btn"}
                  variant="contained"
                  color="primary"
                  style={{ marginLeft: 10 }}
                  onClick={handleClose}
                >
                  Cancel
                </Button>

                {!isNullOrEmpty(wardName) && wardRooms.length > 0 && (
                  <Button
                    data-test={"ward-add-edit-save-btn"}
                    variant="contained"
                    color="primary"
                    style={{ marginLeft: 10 }}
                    onClick={handleSave}
                  >
                    Save
                  </Button>
                )}
              </Grid>
            </Grid>
          </>
        )}
      </Box>
      <WardAddEditRoomDialog
        open={roomDialogOpen}
        onClose={() => setRoomDialogOpen(false)}
        onSave={handleSaveRoom}
        roomName={roomName}
        setRoomName={setRoomName}
        roomGender={roomGender}
        setRoomGender={setRoomGender}
        beds={beds}
        setBeds={setBeds}
        editingBedIndex={editingBedIndex}
        setEditingBedIndex={setEditingBedIndex}
        roomID={roomID}
      />
      <Dialog
        open={confirmDeleteDialogOpen}
        onClose={() => setConfirmDeleteDialogOpen(false)}
      >
        <DialogTitle data-test={"confirm-delete-title"}>
          Confirm Delete
        </DialogTitle>
        <DialogContent data-test={"confirm-delete-subtitle"}>
          Are you sure you want to delete this ?
        </DialogContent>
        <DialogActions>
          <Button
            data-test={"confirm-delete-no-btn"}
            onClick={() => setConfirmDeleteDialogOpen(false)}
            color="primary"
          >
            No
          </Button>
          <Button
            data-test={"confirm-delete-yes-btn"}
            onClick={confirmDeleteRoom}
            color="primary"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
